// Renders one order: summary plus its collapsible shipment, address and item sections.

import { useState } from 'react';
import { Package, Truck } from 'lucide-react';
import { TruncatedText } from '@/components/shared/truncated-text';
import { CollapseBody, CollapseHeader } from './collapse-toggle';
import { HeaderLink } from './header-link';
import { statusStyle } from './status-style';
import { cn } from '@/lib/utils';
import { SectionCard } from './section-card';
import { hasText } from './has-text';
import { ShippingAddressSection } from './shipping-address-section';
import { StatTile } from './stat-tile';
import type { OrderItemDto, OrderItemRefundDto, OrderOtherRefundDto, WorkspaceOrderDto } from './types';

/** The order number's type; the header link measures an invisible copy of it. */
const TITLE = 'text-[15px] font-medium leading-[1.1] text-text-body';

const REFUND_LABEL: Record<OrderItemRefundDto['kind'], string> = { removed: 'Removed', returned: 'Returned', refunded: 'Refunded' };

/** "Removed" when the refund took the whole line, "Refunded 1 of 2" when it took part of it. */
function refundLabel(refund: OrderItemRefundDto, item: OrderItemDto): string {
  const label = REFUND_LABEL[refund.kind];
  return refund.quantity >= item.quantity ? label : `${label} ${refund.quantity} of ${item.quantity}`;
}

/** The refund's date and note, for the chip's hover text. */
function refundDetail(refund: Pick<OrderOtherRefundDto, 'date' | 'note'>): string {
  return [refund.date, refund.note && `"${refund.note}"`].filter(Boolean).join(' · ');
}

function fullyRefunded(item: OrderItemDto): boolean {
  return (item.refunds ?? []).reduce((sum, refund) => sum + refund.quantity, 0) >= item.quantity;
}

export function OrderCard({ order }: { order: WorkspaceOrderDto }) {
  const [open, setOpen] = useState(true);
  // A section with nothing in it is noise, so each one renders only when it has data.
  const shipment = hasText(order.shipment) ? order.shipment : null;
  const address = hasText(order.shippingAddress) ? order.shippingAddress : null;
  const otherRefunds = order.otherRefunds ?? [];
  const hasDetails = order.items.length > 0 || Boolean(order.shippingCost) || otherRefunds.length > 0;

  return (
    <div className="flex w-full flex-col rounded-xl border border-gray-100 bg-white px-2 pb-[18px] pt-2 drop-shadow-[0_0_3px_rgba(0,0,0,0.06)]">
      <div className="flex w-full flex-col gap-2">
        {/* One column pinned to the card: a grid cell grows to its content, and three status chips are wider than the card. */}
        <div className="grid grid-cols-1">
          <CollapseHeader open={open} label={`order ${order.externalId}`} onToggle={() => setOpen((prev) => !prev)} className="min-w-0 items-start [grid-area:1/1]">
          <div className="flex min-w-0 flex-col gap-2">
            <p className={cn(TITLE, order.url && 'mr-5')}>{order.externalId}</p>
            <div className="flex flex-wrap items-center gap-1">
              {order.statuses.map((status) => (
                <span
                  key={status}
                  className={cn('rounded-xl px-3 py-1 text-[12px] font-medium leading-[1.1]', statusStyle(status))}
                >
                  {status}
                </span>
              ))}
            </div>
          </div>
          </CollapseHeader>
          {order.url && <HeaderLink title={order.externalId} href={order.url} label="View order" titleClassName={TITLE} />}
        </div>
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex flex-col gap-1.5 text-[12px] leading-[1.1]">
            <p className="text-gray-700">Ordered on</p>
            <p className="font-semibold text-text-prose">{order.orderedOn}</p>
          </div>
          {order.totalSpent && <StatTile value={order.totalSpent} label="Total spent" bordered />}
        </div>
        {order.cancelledOn && (
          <div className="flex w-full items-start justify-between gap-3 text-[12px] leading-[1.1]">
            <p className="shrink-0 text-gray-700">Cancelled on</p>
            <p className="text-right font-semibold text-text-prose">
              {order.cancelledOn}
              {order.cancelReason && <span className="font-normal text-gray-700">{` · ${order.cancelReason}`}</span>}
            </p>
          </div>
        )}
      </div>

      <CollapseBody open={open}>
        <div className="flex w-full flex-col gap-4 pt-4">
          {shipment && (
            <SectionCard icon={<Truck className="size-3.5" />} title="Shipment">
              <div className="flex flex-col gap-3">
                {(shipment.provider || shipment.trackingId) && (
                  <div className="flex items-start gap-3">
                    {shipment.provider && (
                      <div className="flex flex-col gap-1">
                        <p className="text-[12px] font-medium leading-[1.1] text-gray-700">Provider</p>
                        <p className="text-[12px] leading-[1.1] text-text-body">{shipment.provider}</p>
                      </div>
                    )}
                    {shipment.trackingId && (
                      <div className="flex min-w-0 flex-col gap-1">
                        <p className="text-[12px] font-medium leading-[1.1] text-gray-700">Tracking ID</p>
                        <TruncatedText text={shipment.trackingId} lines={2} className="text-[12px] leading-[1.1] text-text-body" />
                      </div>
                    )}
                  </div>
                )}
                {/* Own row: a long tracking number (DHL sends 30 digits) squeezed the link out beside it. */}
                {shipment.link && (
                  <div className="flex min-w-0 flex-col gap-1">
                    <p className="text-[12px] font-medium leading-[1.1] text-gray-700">Link</p>
                    <TruncatedText
                      text={shipment.link}
                      href={shipment.link}
                      lines={2}
                      className="text-[12px] leading-[1.1] text-blue-500"
                    />
                  </div>
                )}
              </div>
            </SectionCard>
          )}

          {shipment && address && <div className="h-px w-full bg-gray-200" />}

          {address && <ShippingAddressSection address={address} />}

          {(shipment || address) && hasDetails && <div className="h-px w-full bg-gray-200" />}

          {hasDetails && (
            <SectionCard icon={<Package className="size-3.5" />} title="Order Details">
              <div className="flex flex-col gap-2.5">
                <div className="flex w-full items-center justify-between text-[12px] font-medium leading-[1.1] text-gray-600">
                  <p>Products</p>
                  <p className="w-[52px] text-right">Price</p>
                </div>
                {order.items.map((item, index) => (
                  <div key={index} className="flex flex-col gap-2.5">
                    {index > 0 && <div className="h-px w-full bg-gray-200" />}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                        <div className="flex min-w-0 items-start gap-1 text-[12px] leading-[1.1]">
                          <span className="shrink-0 font-medium text-gray-700">{item.quantity}x</span>
                          <TruncatedText text={item.name} lines={2} className="min-w-0 text-[12px] leading-[1.1] text-text-body" />
                        </div>
                        {(item.variant || item.sku) && (
                          <p className="text-[11px] leading-[1.1] text-gray-700">
                            {[item.variant, item.sku && `SKU ${item.sku}`].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {item.refunds && (
                          <div className="flex flex-wrap gap-1">
                            {item.refunds.map((refund, refundIndex) => (
                              <span
                                key={refundIndex}
                                title={refundDetail(refund) || undefined}
                                className={cn('rounded-xl px-2 py-0.5 text-[11px] font-medium leading-[1.1]', statusStyle('refunded'))}
                              >
                                {refundLabel(refund, item)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <p
                        className={cn(
                          'shrink-0 text-right text-[12px] font-medium leading-[1.1] text-text-prose',
                          fullyRefunded(item) && 'text-gray-500 line-through',
                        )}
                      >
                        {item.price}
                      </p>
                    </div>
                  </div>
                ))}
                {otherRefunds.map((refund, index) => {
                  const detail = refundDetail(refund);
                  return (
                    <div key={`refund-${index}`} className="flex flex-col gap-2.5">
                      {(order.items.length > 0 || index > 0) && <div className="h-px w-full bg-gray-200" />}
                      <p className="text-[12px] leading-[1.1] text-text-body">
                        <span className="font-medium">Refund</span>
                        {` · ${refund.items.length > 0 ? refund.items.join(', ') : 'no items'}`}
                        {detail && <span className="text-gray-700">{` · ${detail}`}</span>}
                      </p>
                    </div>
                  );
                })}
                {order.shippingCost && (
                  <>
                    <div className="h-px w-full bg-gray-200" />
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-1 text-[12px] leading-[1.1]">
                        <p className="text-text-body">Shipping Cost</p>
                        <p className="italic text-gray-700">{order.shippingCost.label}</p>
                      </div>
                      <p className="w-[50px] text-right text-[12px] font-medium leading-[1.1] text-text-prose">
                        {order.shippingCost.amount}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </SectionCard>
          )}
        </div>
      </CollapseBody>
    </div>
  );
}
